import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Heading1 } from "lucide-react";
import { CharacterCountBar } from "./CharacterCountBar";
import { H1_LIMITS } from "./seoCharLimits";
import type {
  PageIssue,
  ProposedFix,
  H1Fix,
  DuplicateH1Details,
} from "@/types/technicalAudit";

// Editable proposed H1 for the duplicate_h1 issue. This fix rewrites the page's
// VISIBLE on-page H1 heading so it is unique across the cluster of pages sharing
// it — it does NOT touch the SEO/meta title tag (Yoast/RankMath). It therefore
// emits `new_h1` only (never `new_seo_title`), mirroring the SeoMetaEditCard
// edit/emit idiom. Themed with coral/teal tokens, not raw palette classes.

interface DuplicateH1EditCardProps {
  issue: PageIssue;
  onEdit: (value: ProposedFix) => void;
}

export function DuplicateH1EditCard({ issue, onEdit }: DuplicateH1EditCardProps) {
  const details = (issue.issue_details || {}) as Partial<DuplicateH1Details>;
  const proposed = issue.proposed_fix as (H1Fix & { reasoning?: string }) | null;
  const approved = issue.approved_value as (H1Fix & { reasoning?: string }) | null;
  // Prefer an already-approved value, then the generated suggestion.
  const proposedH1 = approved?.new_h1 || proposed?.new_h1 || "";

  const currentH1 = details.h1_text || "";
  const reasoning = approved?.reasoning || proposed?.reasoning;

  const draftKey = `${issue.id}:${issue.issue_type}:${JSON.stringify(issue.approved_value ?? issue.proposed_fix ?? "")}`;
  const [draft, setDraft] = useState<{ key: string; h1: string } | null>(null);
  const editedH1 = draft?.key === draftKey ? draft.h1 : proposedH1;

  // Push the edited H1 up so Approve/Apply send exactly what the specialist sees.
  // Emit undefined when empty so a cleared field falls back to the stored
  // suggestion instead of overwriting the page H1 with a blank heading.
  useEffect(() => {
    const value: Partial<H1Fix> = { new_h1: editedH1.trim() || undefined };
    onEdit(value as ProposedFix);
  }, [editedH1, onEdit]);

  const hasProposed = Boolean(proposedH1);
  const hasStoredSuggestion = Boolean(
    issue.approved_value || issue.proposed_fix || issue.proposed_value,
  );

  if (!hasProposed) {
    const actionLabel = hasStoredSuggestion ? "Regenerate fix" : "Generate fix";
    const actionVerb = hasStoredSuggestion
      ? "replace the stored placeholder with"
      : "draft";

    return (
      <Card>
        <CardContent className="py-4 text-sm text-muted-foreground">
          Click <span className="font-medium">{actionLabel}</span> above to{" "}
          {actionVerb} a unique on-page H1 heading, then edit it and apply it to
          this page's visible H1 — not the SEO/meta title tag (Yoast/RankMath).
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Heading1 className="h-4 w-4 text-teal-dark" />
          Proposed H1
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Rewrites this page's visible on-page H1 heading so it is unique across
          the{" "}
          {typeof details.count === "number" ? details.count : "duplicate"} pages
          sharing it — not the SEO/meta title tag (Yoast/RankMath).
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {currentH1 && (
          <div>
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Current H1 (duplicated)
            </div>
            <div className="rounded-md border bg-muted/50 p-2 text-sm">
              {currentH1}
            </div>
          </div>
        )}
        <div>
          <div className="mb-1 text-sm font-medium text-teal-dark">
            Proposed H1 (editable)
          </div>
          <Textarea
            value={editedH1}
            onChange={(e) => setDraft({ key: draftKey, h1: e.target.value })}
            className="min-h-[70px]"
            placeholder="Edit the proposed H1 heading..."
          />
          <div className="mt-2">
            <CharacterCountBar
              current={editedH1.trim().length}
              min={H1_LIMITS.min}
              max={H1_LIMITS.max}
              optimal={H1_LIMITS.optimal}
            />
          </div>
        </div>

        {reasoning && (
          <div className="rounded-md bg-teal-light p-3">
            <p className="text-xs font-medium text-teal-dark">AI Reasoning:</p>
            <p className="mt-1 text-xs text-teal-dark">{reasoning}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
